import React, { useEffect, useState } from 'react';
import { sandboxAPI, detectEnvironment } from '../api/sandbox';
import { toast } from 'sonner';
import { RefreshCw, Mail, Trash2, Loader2, Beaker } from 'lucide-react';

/**
 * SandboxTools — Renders only when the API reports ENV != production.
 * Provides:
 *   - Reset Sandbox button (wipes DB and re-runs seed)
 *   - Mock Emails viewer
 */
export default function SandboxTools() {
  const [env, setEnv] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [emails, setEmails] = useState(null);
  const [loadingEmails, setLoadingEmails] = useState(false);

  useEffect(() => {
    detectEnvironment().then(setEnv);
  }, []);

  if (env === null) return null;
  if (env === 'production') return null;

  const handleReset = async () => {
    const ok = window.confirm(
      `¿Resetear sandbox completamente?\n\n` +
      `Esto borrará TODOS los datos de la base "${env}" y volverá a sembrar usuarios, ` +
      `clientes, proyectos, eventos, staff y plantillas demo.\n\n` +
      `Solo afecta el ambiente sandbox. La producción NO se toca.`
    );
    if (!ok) return;

    setResetting(true);
    const tid = toast.loading('Reseteando sandbox y sembrando datos…');
    try {
      const res = await sandboxAPI.reset();
      if (res.ok) {
        toast.success(
          `Sandbox listo. ${res.collections_dropped?.length || 0} colecciones limpiadas y reseed OK.`,
          { id: tid, duration: 6000 }
        );
      } else {
        toast.error(`Reset parcial: ${res.message}`, { id: tid, duration: 8000 });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`Reset falló: ${detail}`, { id: tid, duration: 8000 });
    } finally {
      setResetting(false);
    }
  };

  const handleLoadEmails = async () => {
    setLoadingEmails(true);
    try {
      const res = await sandboxAPI.listMockEmails(50);
      setEmails(res.items || []);
      toast.success(`${res.total} emails capturados`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`No se pudieron cargar: ${detail}`);
    } finally {
      setLoadingEmails(false);
    }
  };

  const handleClearEmails = async () => {
    if (!window.confirm('¿Borrar todos los mock emails?')) return;
    try {
      const res = await sandboxAPI.clearMockEmails();
      toast.success(`${res.deleted} emails eliminados`);
      setEmails([]);
    } catch (err) {
      toast.error('No se pudo limpiar');
    }
  };

  return (
    <div className="rounded-sm border-2 border-yellow-300 bg-yellow-50 overflow-hidden mb-6" data-testid="sandbox-tools">
      <div className="px-5 py-4 border-b border-yellow-200 bg-yellow-50">
        <div className="flex items-center gap-2">
          <Beaker size={18} className="text-yellow-700" />
          <h2 className="font-bold text-yellow-800 text-lg">Herramientas Sandbox</h2>
          <span className="ml-2 px-2 py-0.5 bg-yellow-200 text-yellow-800 text-xs rounded-sm font-mono">
            ENV={env}
          </span>
        </div>
        <p className="text-yellow-700 text-xs mt-1">
          Este panel solo aparece cuando el API reporta <code>env != production</code>.
        </p>
      </div>

      <div className="p-5 space-y-5">
        {/* Reset Sandbox */}
        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex-1">
            <h3 className="font-bold text-soem-navy mb-1">Reset sandbox a estado inicial</h3>
            <p className="text-slate-600 text-sm">
              Borra todos los datos del sandbox y vuelve a sembrar usuarios demo, clientes, proyectos,
              eventos, staff y la plantilla SOEM oficial. Operación segura y reversible (siempre puedes
              volver a resetear).
            </p>
          </div>
          <button
            onClick={handleReset}
            disabled={resetting}
            className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-60 text-white font-bold py-3 px-6 rounded-sm text-sm flex items-center gap-2"
            data-testid="reset-sandbox-btn"
          >
            {resetting ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
            {resetting ? 'Reseteando…' : 'Reset Sandbox'}
          </button>
        </div>

        {/* Mock Emails */}
        <div className="border-t border-yellow-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold text-soem-navy mb-1 flex items-center gap-2">
                <Mail size={16} /> Emails capturados (mock)
              </h3>
              <p className="text-slate-600 text-xs">
                En sandbox los emails NO se envían — se guardan aquí para que puedas inspeccionarlos.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleLoadEmails}
                disabled={loadingEmails}
                className="text-xs bg-white border border-yellow-300 text-yellow-700 px-3 py-1.5 rounded-sm font-semibold hover:bg-yellow-100"
              >
                {loadingEmails ? 'Cargando…' : 'Cargar últimos 50'}
              </button>
              {emails && emails.length > 0 && (
                <button
                  onClick={handleClearEmails}
                  className="text-xs bg-white border border-red-300 text-red-700 px-3 py-1.5 rounded-sm font-semibold hover:bg-red-50 flex items-center gap-1"
                >
                  <Trash2 size={12} /> Limpiar
                </button>
              )}
            </div>
          </div>

          {emails && emails.length === 0 && (
            <p className="text-sm text-slate-500 italic">No hay emails capturados aún.</p>
          )}

          {emails && emails.length > 0 && (
            <div className="border border-yellow-200 rounded-sm overflow-hidden bg-white max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-yellow-100 text-yellow-800 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Para</th>
                    <th className="text-left px-3 py-2">Asunto</th>
                    <th className="text-left px-3 py-2">Cuándo</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((e, i) => (
                    <tr key={i} className="border-t border-yellow-100 hover:bg-yellow-50">
                      <td className="px-3 py-2 font-mono">{e.to}</td>
                      <td className="px-3 py-2">{e.subject}</td>
                      <td className="px-3 py-2 text-slate-500">
                        {e.sent_at ? new Date(e.sent_at).toLocaleString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
